import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadLogoForPDF, addLogoToPDF } from '../pdfLogoUtil';

const IndividualGAD7PDF = ({ clientData, chartRef, user }) => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const pdf = new jsPDF('l', 'mm', 'letter');
      const userName = user?.name || 'Unknown User';
      const timestamp = new Date().toLocaleString();

      const logoDataUrl = await loadLogoForPDF();
      const addLogo = (x, yPos, w, h) => {
        addLogoToPDF(pdf, logoDataUrl, x, yPos, w, h);
      };

      const {
        clientId, currentScore, firstScore, change, percentChange,
        trend, ratePerMonth, durationDays, assessmentCount,
        currentSeverity, expectedScore, expectedSeverity, modelAccuracy,
        responseCategory, firstDate, lastDate
      } = clientData;

      const severityWord = currentSeverity.split(' (')[0];

      // ============================================================
      // PAGE 1: Chart (landscape)
      // ============================================================
      const lPageWidth = pdf.internal.pageSize.getWidth();
      const lPageHeight = pdf.internal.pageSize.getHeight();
      const lMargin = 15;
      let y = lMargin;

      const headerLogoH = 16;
      const headerLogoW = headerLogoH * 3;
      addLogo(lPageWidth - lMargin - headerLogoW, y, headerLogoW, headerLogoH);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text(`Individual GAD-7 Tracker: ${clientId}`, lMargin, y + 6);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, lMargin, y + 12);
      y += headerLogoH + 4;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(lMargin, y, lPageWidth - lMargin, y);
      y += 3;

      // Chart image
      if (chartRef?.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          });
          const chartImgData = canvas.toDataURL('image/png');

          const footerReserve = 5;
          const availableHeight = lPageHeight - y - footerReserve;
          const availableWidth = lPageWidth - lMargin * 2;

          const aspectRatio = canvas.height / canvas.width;
          let imgWidth = availableWidth;
          let imgHeight = imgWidth * aspectRatio;

          if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight / aspectRatio;
          }

          const imgX = lMargin + (availableWidth - imgWidth) / 2;
          pdf.addImage(chartImgData, 'PNG', imgX, y, imgWidth, imgHeight);
        } catch (e) {
          console.error('Chart capture failed:', e);
        }
      }

      // ============================================================
      // PAGE 2+: Analytics (portrait)
      // ============================================================
      pdf.addPage('letter', 'portrait');
      const aPageWidth = pdf.internal.pageSize.getWidth();
      const aPageHeight = pdf.internal.pageSize.getHeight();
      const aMargin = 20;
      y = aMargin;

      const aLogoH = 14;
      const aLogoW = aLogoH * 3;
      addLogo(aPageWidth - aMargin - aLogoW, y, aLogoW, aLogoH);

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Individual GAD-7 Tracker', aMargin, y + 6);
      pdf.text(`Clinical Decision Support: ${clientId}`, aMargin, y + 14);
      y += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, aMargin, y);
      y += 5;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(aMargin, y, aPageWidth - aMargin, y);
      y += 8;

      // FDA Disclaimer
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(120, 80, 0);
      const disclaimerLines = pdf.splitTextToSize(
        'Clinical Decision Support Notice: This analysis provides evidence-informed clinical decision support based on GAD-7 standardized scoring guidelines (Spitzer et al., 2006). All recommendations must be evaluated by a licensed healthcare professional. This is not a substitute for professional medical advice, diagnosis, or treatment.',
        aPageWidth - aMargin * 2
      );
      pdf.text(disclaimerLines, aMargin, y);
      y += disclaimerLines.length * 5 + 4;

      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > aPageHeight - 35) {
          pdf.addPage('letter', 'portrait');
          y = aMargin;
        }
      };

      const sectionHeader = (title) => {
        checkPageBreak(20);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text(title, aMargin, y);
        y += 9;
      };

      const bulletLine = (text, indent = 4) => {
        checkPageBreak(7);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);
        const lines = pdf.splitTextToSize(text, aPageWidth - aMargin * 2 - indent - 4);
        pdf.text(`\u2022  ${lines[0]}`, aMargin + indent, y);
        y += 5;
        for (let i = 1; i < lines.length; i++) {
          checkPageBreak(5);
          pdf.text(`   ${lines[i]}`, aMargin + indent, y);
          y += 5;
        }
      };

      // Anxiety Assessment
      sectionHeader('Anxiety Assessment Analysis');
      bulletLine(`Current GAD-7 Score: ${currentScore} (${currentSeverity})`);
      bulletLine(`Baseline Score: ${firstScore} (${(() => { const [y,m,d] = firstDate.split('-'); return new Date(y, m-1, d).toLocaleDateString(); })()})`);
      bulletLine(`Overall Change: ${change >= 0 ? '+' : ''}${change} points (${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%)`);
      bulletLine(`Trend Pattern: ${trend.charAt(0).toUpperCase() + trend.slice(1)}`);
      bulletLine(`Rate of Change: ${ratePerMonth >= 0 ? '+' : ''}${ratePerMonth.toFixed(1)} points per month`);
      bulletLine(`Total Assessments: ${assessmentCount} over ${durationDays} days`);
      bulletLine(`Response Classification: ${responseCategory}`);
      y += 4;

      // Predictive Analysis
      if (expectedScore !== null) {
        sectionHeader('Predictive Pattern Analysis');
        const forecastDir = expectedScore < currentScore ? 'improve' : expectedScore > currentScore ? 'worsen' : 'remain stable';
        bulletLine(`4-Week Forecast: GAD-7 score of ${expectedScore.toFixed(1)} (expected to ${forecastDir})`);
        bulletLine(`Expected Severity: ${expectedSeverity}`);
        bulletLine(`Model Confidence: ${modelAccuracy}% (based on ${assessmentCount} assessments)`);
        bulletLine(`Sample Adequacy: ${assessmentCount < 5 ? 'Moderate' : assessmentCount < 8 ? 'Good' : 'Excellent'} for pattern detection`);
        y += 4;
      }

      // Treatment Considerations
      sectionHeader(`Treatment Considerations for ${severityWord} Anxiety`);
      bulletLine(`Treatment Focus: ${currentScore <= 4 ? 'Maintenance and Relapse Prevention' : currentScore <= 9 ? 'Active Intervention and Skill Building' : currentScore <= 14 ? 'Comprehensive Evidence-Based Treatment' : 'Intensive Treatment with Comprehensive Support'}`);
      bulletLine(`Current severity with ${trend} trend`);
      bulletLine('CBT: Cognitive behavioral therapy approaches may be beneficial for anxiety treatment');
      bulletLine('Exposure Therapy: Systematic exposure techniques could help reduce avoidance and fear responses');
      bulletLine(`Medication Consultation: ${currentScore >= 10 ? 'Strongly consider' : 'May consider'} psychiatric evaluation`);
      bulletLine('Relaxation Training: Progressive muscle relaxation and breathing techniques may reduce physical anxiety symptoms');
      y += 4;

      // Measurement-Based Care
      const reassessment = currentScore >= 15 ? 'Weekly' : currentScore >= 10 ? 'Every 2-3 weeks' : 'Monthly';
      sectionHeader('Measurement-Based Care Protocol');
      bulletLine(`GAD-7 Reassessment: ${reassessment} monitoring may be appropriate`);
      bulletLine('Progress Evaluation: Consider comprehensive review after 4-6 weeks');
      bulletLine('Treatment Adjustment: May warrant consideration if minimal improvement after 6-8 weeks');
      bulletLine('Safety Monitoring: Standard anxiety assessment as clinically indicated');
      bulletLine('Remission Goal: GAD-7 score <5 represents clinical remission');
      y += 4;

      // Clinical Outcome
      sectionHeader('Clinical Outcome Assessment');
      bulletLine(`Response Category: ${responseCategory}`);
      bulletLine(`Remission Status: ${currentScore < 5 ? 'Achieved' : 'Not yet achieved'}`);
      bulletLine(`Clinical Significance: ${Math.abs(change) >= 5 ? 'Clinically significant' : Math.abs(change) >= 3 ? 'Moderate' : 'Minimal'} change`);

      // Disclaimer at bottom
      checkPageBreak(22);
      y += 6;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      const footerDisclaimer = pdf.splitTextToSize(
        'DISCLAIMER: All treatment recommendations require clinical judgment and individualized assessment by licensed healthcare professionals. This analysis provides informational support only and is not a substitute for professional medical advice, diagnosis, or treatment.',
        aPageWidth - aMargin * 2
      );
      pdf.text(footerDisclaimer, aMargin, y);

      pdf.save(`GAD7_Tracker_${clientId}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasData = clientData && clientData.chartPoints && clientData.chartPoints.length >= 2;

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !hasData}
      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
        generating
          ? 'bg-gray-300 text-gray-500 cursor-wait'
          : !hasData
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
      }`}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default IndividualGAD7PDF;
